import { formatAsOfFilterDate } from '@dealstack/frontend/shared/utils/utc.ts';
import { Dispatch, SetStateAction, useCallback, useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { ExcelFormat } from '@dealstack/shared/lib/common/file/file.ts';
import { downloadCapExcelQuery } from '@dealstack/frontend/app/services/CapTableData.service.ts';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile.ts';
import { buildExportFileName } from '@dealstack/frontend/shared/files/exportFileName.utils';
import { CapHeaderContext } from '@dealstack/frontend/app/pages/Project/Cap/common/CapPageHeader/useCapPageHeaderController.tsx';
import { CapTableRail } from '@dealstack/shared/lib/generated';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';

export const useDownloadCapTableExcel = ({
	dealName,
	dealId,
	rail,
	setIsDownloadingExcel,
}: {
	dealName: string;
	dealId: string;
	// CAP-1999: the rail currently on screen. Without it the export always came back on the legal
	// rail, so exporting a Beneficial cap table handed the user the other rail's figures.
	rail: CapTableRail;
	setIsDownloadingExcel: Dispatch<SetStateAction<boolean>>;
}) => {
	const { filterDate } = useContext(CapHeaderContext);

	const filterDateToString = formatAsOfFilterDate(filterDate) || '';

	const downloadExcelRequest = useMutation({
		mutationKey: [
			API_PATHS.CAP_DOWNLOAD_EXCEL_GET,
			{ dealId, format: ExcelFormat.XLSX, filterDate: filterDateToString, rail },
		],

		mutationFn: downloadCapExcelQuery,
	});

	const onDownloadCapTableToExcel = useCallback(
		async (format: ExcelFormat = ExcelFormat.XLSX) => {
			setIsDownloadingExcel(true);
			await downloadExcelRequest.mutateAsync(
				{ format, filterDate: filterDateToString, rail },
				{
					onSuccess: (blob) =>
						downloadFileFromBlob(
							blob,
							buildExportFileName(
								'Cap Table',
								dealName,
								filterDate ?? new Date(),
								format
							)
						),
					onError: (error: unknown) => {
						const reason = getUserFacingErrorMessage(error) ?? 'an unexpected error';
						alert(
							`Failed to download file with error: ${reason}. Please check the console for more details.`
						);
					},
					onSettled: () => {
						setIsDownloadingExcel(false);
					},
				}
			);
		},
		[dealId, dealName, filterDate, filterDateToString, rail]
	);

	return { onDownloadCapTableToExcel };
};
